import type { IntlShape } from "react-intl";
import { type ZodObject, type ZodTypeAny, z } from "zod";
import { getMaxBasedOnUnit, parseNumber } from "../../../utils";
import type InputType from "../../Input/InputType";
import { JsonInputType } from "../../Input/JsonInputType";
import { SideName } from "../../Side/SideName";
import Plate from "../Plate";
import Polygon from "./Polygon";

export default class Star extends Plate {
	public static code = "plate_star";

	public getDimensionsString(input: InputType, intl: IntlShape): string | undefined {
		if (input.width_1 !== undefined && input.width_2 !== undefined && input.points !== undefined) {
			const { width_1, width_2 } = this.formatInputs(input, intl);
			const { points } = this.formatInputs(input, intl, "decimal");

			return intl.formatMessage(
				{
					defaultMessage: "Buiten ø {width_1}, binnen ø {width_2} met {points} punten",
					id: "XV5Cqv",
				},
				{ width_1, width_2, points },
			);
		}

		return undefined;
	}

	protected getDimensionSchema(): ZodTypeAny {
		const inputs = super.getDimensionSchema() as ZodObject<Record<string, ZodTypeAny>>;
		const points = this.getInputById(JsonInputType.Points);
		const min = points?.min ?? 1;
		const max = (points?.max ? getMaxBasedOnUnit(points.max, this.unit) : 0) ?? 100;

		return inputs
			.extend({
				points: z.preprocess(parseNumber, z.number()).superRefine((value, context) => {
					if (value < min || value > max) {
						context.addIssue({
							code: z.ZodIssueCode.custom,
							params: {
								type: "between_values",
								min: min,
								max: max,
							},
							path: [JsonInputType.Points],
						});
					}
				}),
			})
			.superRefine((value, context) => {
				if (value.width_2 >= value.width_1) {
					context.addIssue({
						code: z.ZodIssueCode.custom,
						params: {
							type: "difference_two_fields_maximum",
							field_1: [this.getInputById(JsonInputType.WidthSection2)?.label],
							field_2: [this.getInputById(JsonInputType.WidthSection1)?.label],
						},
						path: ["width_2"],
					});
				}
			});
	}

	public calculateSideLength(side: SideName, input: InputType) {
		const outerDiameter = Number(input[JsonInputType.WidthSection1]);
		const innerDiameter = Number(input[JsonInputType.WidthSection2]);
		const points = Number(input[JsonInputType.Points]);

		if (!(Number.isNaN(outerDiameter) || Number.isNaN(innerDiameter) || Number.isNaN(points))) {
			switch (side) {
				case SideName.Length1: {
					const baseLength = innerDiameter * (2 * Math.sin(Math.PI / points));
					const spikeHeight =
						(outerDiameter - innerDiameter * (2 * Math.tan(Math.PI / points))) / 2;
					const edgeLength = ((baseLength / 2) ** 2 + spikeHeight ** 2) ** 0.5;

					return 2 * points * edgeLength;
				}
			}
		}

		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const width = Number(input[JsonInputType.WidthSection1]);

		if (!Number.isNaN(width)) {
			return { length: width, width };
		}
	}

	protected calculateMaxBeltSize(data: Record<JsonInputType, number>) {
		const { length, width } = Polygon.calculatePackingDimensions(
			data.width_1,
			data.points,
			this.product?.grain_type === null,
		);

		return super.calculateMaxBeltSize({
			[JsonInputType.Length]: length,
			[JsonInputType.Width]: width,
		} as Record<JsonInputType, number>);
	}
}
